import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Button from "../../components/Button/Button";
import {
  TextBody,
  TextSecondary,
} from "../../components/DesignSystem/DesignSystemText";
import KudosPreview from "../CreateKudos/KudosPreview";
import KudosDetailsModal from "./KudosDetailsModal";
import { ProfileTab } from "./useProfile";
import React from "react";
import Image from "../../components/Image/Image";
import InfiniteScroll from "react-infinite-scroll-component";
import { cx } from "@emotion/css";

type Token = {
  tokenId: string;
  headline: string;
  imageUrl: string;
  uniqId: string;
};

interface Props {
  tokens: Token[];
  onClickPin?: (x: string) => void;
  onClickHide?: (x: string) => void;
  onClickBurn?: (x: string) => void;
  hideText?: string;
  tab?: any;
  isClaim?: boolean;
  highlighted?: Token[];
  loadMore?: () => void;
  hasMore?: boolean;
}

const KudosGrid = ({
  tokens,
  onClickPin,
  onClickHide,
  onClickBurn,
  tab,
  isClaim,
  highlighted,
  hideText,
  loadMore,
  hasMore,
}: Props) => {
  const tokenArray = tokens ?? [];

  const [show, setShow] = useState(false);
  const [selectedKudos, setSelectedKudos] = useState("");
  const router = useRouter();
  const searchParams = router.query;
  const setSearchParams = (params: any) => {
    const query = {
      tab: params.tab,
      ...searchParams,
      tokenId: params.tokenId,
    };

    if (!params.tokenId) {
      delete query.tokenId;
    }

    router.push(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { shallow: true },
    );
  };

  const onClickKudos = (token: Token) => {
    if (isClaim) {
      router.push(`/claim/${token.uniqId}`, undefined, { shallow: true });
      return;
    }
    toggleOverflow(true);
    setSearchParams({ tokenId: token.tokenId, tab: tab });
    setShow(true);
  };

  useEffect(() => {
    const tokenId = (searchParams?.tokenId as string) ?? "";
    const tab = searchParams?.tab ?? "";

    if (tokenId) {
      toggleOverflow(true);
      setSelectedKudos(tokenId);
      setShow(true);
    } else {
      setSelectedKudos("");
      toggleOverflow(false);
    }
  }, [searchParams]);

  const isTokenHighlighted = (tokenId: string) =>
    highlighted?.map((token: any) => token.tokenId)?.includes(tokenId) || false;

  function toggleOverflow(isShowing: boolean): void {
    const html = document.getElementById("html");
    if (isShowing) {
      // @ts-ignore
      html.style.overflow = "hidden";
    } else {
      // @ts-ignore
      html.style.overflow = "auto";
    }
  }

  const onCloseModal = (event: React.MouseEvent) => {
    let dataValue = (event.target as HTMLElement).getAttribute("data-value");

    if (dataValue !== "modalOverlay") return;
    toggleOverflow(false);
    setSearchParams({ tab: tab });
    setShow(false);
  };

  const onCloseModalWithBtn = () => {
    toggleOverflow(false);
    setSearchParams({ tab: tab });
    setShow(false);
  };

  return (
    <>
      <InfiniteScroll
        className={cx("m-[-32rem] p-[32rem]", grid)}
        dataLength={tokens?.length || 0}
        next={async () => {
          if (!!loadMore) {
            await loadMore();
          }
        }}
        hasMore={hasMore ? hasMore : true}
        loader={null}
      >
        {tokenArray.map((token: Token, index: number) => {
          return (
            <div
              key={token.tokenId + "-" + index}
              className="flex flex-col items-center justify-start gap-y-6 max-w-[300px]"
            >
              <KudosPreviewWrapper
                imageUrl={token.imageUrl}
                onClickImage={() => {
                  onClickKudos(token);
                }}
                onClickPin={
                  (onClickPin && !isTokenHighlighted(token.tokenId)) ||
                  tab === ProfileTab.Highlights
                    ? () => {
                        if (onClickPin) {
                          onClickPin(token.tokenId);
                        }
                      }
                    : undefined
                }
                onClickHide={
                  onClickHide
                    ? () => {
                        if (onClickHide) {
                          onClickHide(token.tokenId);
                        }
                      }
                    : undefined
                }
                onClickBurn={
                  onClickBurn
                    ? () => {
                        if (onClickBurn) {
                          onClickBurn(token.tokenId);
                        }
                      }
                    : undefined
                }
                hideText={hideText}
              />
              <TextSecondary className="whitespace-nowrap text-center">
                <Button
                  theme="custom"
                  onClick={() => {
                    onClickKudos(token);
                  }}
                >
                  {token.headline}
                </Button>
              </TextSecondary>
            </div>
          );
        })}
      </InfiniteScroll>

      {selectedKudos && (
        <KudosDetailsModal
          show={show}
          onCloseModal={onCloseModal}
          onCloseModalWithBtn={onCloseModalWithBtn}
          onChangeKudos={(token) => {
            setSearchParams({ tokenId: token, tab: tab });
          }}
          selectedKudos={selectedKudos}
          kudos={tokens}
        />
      )}
    </>
  );
};

const grid =
  "flex gap-10 flex-col items-center sm:flex-row sm:items-start sm:flex-wrap overflow-auto";

const KudosPreviewWrapper = ({
  imageUrl,
  onClickImage,
  onClickPin,
  onClickHide,
  onClickBurn,
  hideText,
}: any) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <>
      <Button
        className="relative"
        onMouseEnter={() => {
          setIsHovered(true);
        }}
        onMouseLeave={() => {
          setIsHovered(false);
        }}
        theme="custom"
        onClick={onClickImage}
      >
        <KudosPreview
          className="!max-h-[300px] !max-w-[300px]"
          src={imageUrl}
          controls={false}
        />

        {isHovered && onClickHide ? (
          <div
            onClick={(e) => {
              try {
                e.stopPropagation();
                onClickHide();
              } catch (error: any) {
                toast.error(error.message);
              }
            }}
            className="bg-black/40 px-[16rem] h-11 flex items-center absolute top-[12px] right-[20rem] hover:bg-black/60 cursor-pointer rounded-lg"
          >
            <TextBody className="!text-white !font-semibold">
              {hideText || "Hide"}
            </TextBody>
          </div>
        ) : (
          <></>
        )}
        {isHovered && onClickBurn ? (
          <div
            onClick={(e) => {
              try {
                e.stopPropagation();
                onClickBurn();
              } catch (error: any) {
                toast.error(error.message);
              }
            }}
            className="bg-black/40 h-11 w-11 p-2 absolute top-[12px] right-[12rem] flex items-center justify-center rounded-[50%] hover:bg-black/60 cursor-pointer"
          >
            <Image
              src="/images/icons/trash.svg"
              height="20rem"
              width="20rem"
              alt=""
              priority
            />
          </div>
        ) : (
          <></>
        )}

        {isHovered && onClickPin ? (
          <div
            onClick={(e) => {
              try {
                e.stopPropagation();
                onClickPin();
              } catch (error: any) {
                toast.error(error.message);
              }
            }}
            className={cx(
              "bg-black/40 h-11 w-11 p-2 absolute top-[12px] flex items-center justify-center rounded-[50%] hover:bg-black/60 cursor-pointer",
              onClickBurn ? "right-[60rem]" : "right-[12rem]",
            )}
          >
            <Image
              src="/images/icons/pin.svg"
              height="24rem"
              width="24rem"
              alt=""
              priority
            />
          </div>
        ) : (
          <></>
        )}
      </Button>
    </>
  );
};

export default KudosGrid;
